import { useMemo } from "react";
import CampaignForm from "./CampaignForm";
import {
  Box,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import ConfirmStep from "./ConfirmStep";
import AdList from "./AdList";
import AdForm from "./AdForm";
import AdCreativeForm from "./AdCreativeForm";
import { Ad } from "../../../../../actions";
import AdsetList from "./AdsetList";
import AdsetHome from "./AdsetHome";
import AdsetListStep from "./AdsetListStep";

export type Step =
  | "none"
  | "create_campaign"
  | "edit_campaign"
  | "list_adsets"
  | "create_adset"
  | "edit_adset"
  | "adset"
  | "create_creative"
  | "edit_creative"
  | "list_ads"
  | "confirm";

export type Campaign = {
  id?: string;
  name: string;
  adsets?: any[];
  ad_account_id: string;
  campaign_type: string;
  event_id: string;
  status?: string;
  // platform: string;
};

export type Adset = {
  ads: Ad[];
  pixel_id?: string;
  status?: string;
  event_id?: string;
  organization_id?: string;
  targeting: {
    geo_locations: {
      countries: string[];
      geo_markets: {
        key: string;
        name: string;
      }[];
    };
    interests: {
      id: string;
      name: string;
    }[];
    custom_audiences?: string[];
    age_min?: number;
    age_max?: number;
  };
  stop_time: string;
  start_time: string;
  optimization_goal: string;
  custom_audience_files?: {
    id?: string;
    external_id?: string;
    file_key: string;
    name: string;
    description: string;
  }[];
  lifetime_budget: {
    amount: number;
    currency: string;
  };
  id?: string;
  name?: string;
  external_id?: string;
  campaign_id?: string;
};

export type StepProps = {
  campaign: Campaign;
  ad?: Ad;
  adset?: Adset;
  setDefaultEditingAdset?: (adset: Adset) => void;
  setDefaultEditingAd?: (ad: Ad) => void;
  pages?: { id: string; name: string }[];
  instagramAccounts?: { id: string; username: string }[];
  adAccounts?: { id: string; name: string }[];
  audiences?: { id: string; name: string }[];
  pixels?: { id: string; name: string }[];
  search?: {
    interests: (query: string) => Promise<{
      data: { id: string; name: string }[];
    }>;
    geo_markets: (query: string) => Promise<{
      data: { key: string; name: string; country_code: string }[];
    }>;
  };
  adsets?: { id: string; name: string }[];
  campaigns?: Campaign[];
  currency?: string;
  createUploadUrl: (
    file: File,
    callback: (params: { signedUrl: string }) => void
  ) => void;
  onCreateCampaign: (campaign: any) => Promise<void>;
  onCreateAd: (ad: any) => Promise<void>;
  onCreateAdCreative: (adCreative: any) => Promise<void>;
  onPublish: (
    campaign: any,
    extra?: { publish_all_adsets?: boolean }
  ) => Promise<void>;
  navigateTo: (step: Step) => void;
  back: () => void;
  next: () => void;
  close: () => void;
  steps: Step[];
  step: Step;
  loading: {
    adsets: boolean;
    campaigns: boolean;
    audiences: boolean;
    pixels: boolean;
    preview: boolean;
    publish: boolean;
    adaccounts: boolean;
    pages: boolean;
    instagramAccounts: boolean;
  };
};

const stepToComponent: Record<Step, React.ElementType<StepProps>> = {
  none: () => null,
  create_campaign: CampaignForm,
  edit_campaign: CampaignForm,
  list_adsets: AdsetListStep,
  create_adset: AdForm,
  adset: AdsetHome,
  edit_adset: AdForm,
  create_creative: AdCreativeForm,
  edit_creative: AdCreativeForm,
  list_ads: AdList,
  confirm: ConfirmStep,
};

const labelForStep = (step: Step) => {
  switch (step) {
    case "create_campaign":
      return "Create campaign";
    case "edit_campaign":
      return "Edit campaign";
    case "create_adset":
      return "Create adset";
    case "edit_adset":
      return "Edit adset";
    case "list_adsets":
      return "List adsets";
    case "adset":
      return "Adset";
    case "create_creative":
      return "Create creative";
    case "edit_creative":
      return "Edit creative";
    case "list_ads":
      return "List ads";
    case "confirm":
      return "Review";
  }
};

const stepsForFlow = (steps: Step[]) => {
  if (steps.at(0) === "create_campaign") {
    return [
      "create_campaign",
      "create_adset",
      "create_creative",
      "list_ads",
      "confirm",
    ];
  }

  const step = steps.at(-1);

  if (["adset", "edit_adset", "list_ads", "edit_creative"].includes(step)) {
    return [step];
  }

  if (step === "edit_campaign") {
    return ["edit_campaign"];
  }

  return [step];
};

const CreateCampaignWizard = ({
  onCreateCampaign,
  step,
  steps,
  setStep,
  defaultEditingAdset,
  onPublish,
  onCreateAd,
  onCreateAdCreative,
  defaultCampaign,
  audiences,
  pixels,
  campaigns,
  instagramAccounts,
  adsets,
  adAccounts,
  close,
  currency,
  search,
  pages,
  createUploadUrl,
  refetchAdsets,
  setDefaultEditingAdset,
  setDefaultEditingAd,
  loading,
  defaultAd,
}: {
  step?: Step;
  steps: Step[];
  setStep: (step: Step) => void;
  close: () => void;
  onCreateCampaign: (campaign: any) => Promise<{ campaign: Campaign } | null>;
  onPublish: (
    campaign: any,
    extra?: { publish_all_adsets?: boolean }
  ) => Promise<void>;
  setDefaultEditingAdset: (adset: Adset) => void;
  setDefaultEditingAd: (ad: Ad) => void;

  defaultCampaign?: Campaign;
  defaultEditingAdset?: Adset;
  defaultAd?: Ad;

  onCreateAd: (ad: any) => Promise<void>;
  onCreateAdCreative: (data: any) => Promise<void>;
  refetchAdsets: (campaignId: string) => Promise<void>;
  audiences?: { id: string; name: string }[];
  search?: StepProps["search"];
  pixels?: {
    id: string;
    name: string;
  }[];
  campaigns?: Campaign[];
  instagramAccounts?: {
    id: string;
    username: string;
  }[];
  pages?: {
    id: string;
    name: string;
  }[];
  adsets?: {
    id: string;
    name: string;
  }[];
  adAccounts?: { id: string; name: string }[];
  currency?: string;
  createUploadUrl: (
    file: File,
    callback: (params: { signedUrl: string }) => void
  ) => void;
  loading?: {
    adsets: boolean;
    campaigns: boolean;
    audiences: boolean;
    pixels: boolean;
    preview: boolean;
    publish: boolean;
    adaccounts: boolean;
    search: boolean;
    instagramAccounts: boolean;
    pages: boolean;
  };
}) => {
  const _onCreateCampaign = async (campaign: any) => {
    return onCreateCampaign(campaign).then((response) => {
      if (step === "create_campaign") {
        setStep("create_adset");
      } else if (step === "edit_campaign") {
        setStep("none");
      }
    });
  };

  const isCreateCampaign = steps.at(0) === "create_campaign";

  const _onCreateAd = (ad: any) => {
    return onCreateAd(ad)
      .then(() => {
        if (step === "create_adset") {
          setStep("create_creative");
        } else if (step === "edit_adset") {
          setStep("none");
        }
        refetchAdsets(defaultCampaign?.id);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const _onCreateAdCreative = (adCreative: any) => {
    return onCreateAdCreative(adCreative)
      .then(() => {
        if (step === "create_creative") {
          setStep("list_ads");
        } else if (step === "edit_creative") {
          setStep("none");
        }
        return refetchAdsets(defaultCampaign?.id);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const _onPublish = (
    campaign: { id: string },
    extra: { publish_all_adsets?: boolean } = {}
  ) => {
    close();
    return onPublish(campaign, extra);
  };
  const Component = useMemo(() => stepToComponent[step], [step]);

  const bubbles = stepsForFlow(steps);
  return (
    <Modal
      size={["initial", "full"]}
      isOpen={step !== "none"}
      onClose={close}
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        mb={[0, "auto"]}
        w="full"
        borderBottomLeftRadius={[0, "md"]}
        borderBottomRightRadius={[0, "md"]}
      >
        <ModalHeader paddingTop={4}>
          <VStack gap={0} width={"full"}>
            <Text w="full">
              {defaultCampaign
                ? `Edit - ${defaultCampaign.name}`
                : "Create a campaign"}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton onClick={close} />
        <ModalBody textAlign={"center"} w="full" minW={["full", 600]}>
          <VStack w="full" spacing={8}>
            <HStack
              w="full"
              alignItems={"flex-start"}
              gap={8}
              justifyContent={"flex-start"}
            >
              {bubbles.map((stepToRender) => (
                <VStack
                  key={stepToRender}
                  w={16}
                  alignItems={"center"}
                  justifyContent={"center"}
                  flexWrap={"wrap"}
                  // onClick={() => setStep(stepToRender as Step)}
                >
                  <Box
                    backgroundColor={
                      stepToRender === step ? "green.500" : "gray.50"
                    }
                    width={8}
                    height={8}
                    borderRadius="full"
                    aspectRatio={1}
                    borderWidth={1}
                    borderColor={"gray.200"}
                  ></Box>
                  <Text key={stepToRender} fontSize={"sm"} textAlign={"center"}>
                    {labelForStep(stepToRender as Step)}
                  </Text>
                </VStack>
              ))}
            </HStack>
            <VStack w="full" padding={4} background={"gray.50"} rounded="md">
              <Component
                campaign={defaultCampaign}
                navigateTo={(step) => {
                  if (step === "create_adset") {
                    setDefaultEditingAdset(null);
                  }

                  if (step === "create_creative") {
                    setDefaultEditingAd(null);
                  }

                  if (step in stepToComponent) {
                    setStep(step);
                    return;
                  }

                  console.log(`No step found for ${step}`);
                }}
                setDefaultEditingAdset={setDefaultEditingAdset}
                adset={defaultEditingAdset}
                steps={steps}
                step={step}
                ad={defaultAd}
                setDefaultEditingAd={setDefaultEditingAd}
                pages={pages}
                onCreateCampaign={_onCreateCampaign}
                onCreateAd={_onCreateAd}
                onCreateAdCreative={_onCreateAdCreative}
                onPublish={_onPublish}
                loading={loading}
                instagramAccounts={instagramAccounts}
                adAccounts={adAccounts}
                close={close}
                back={() => {
                  // if ([
                  //   "edit_campaign",
                  //   "edit_adset",
                  //   "edit_creative",
                  // ].includes(step)) {
                  //   setStep("none");
                  //   return;
                  // }

                  if (step === "edit_adset" || step === "create_adset") {
                    setStep("adset");
                    return;
                  }

                  if (step === "adset") {
                    setStep("list_adsets");
                    return;
                  }

                  if (step === "edit_creative") {
                    setStep("list_ads");
                    return;
                  }

                  if (step === "create_creative") {
                    setStep("list_ads");
                    return;
                  }

                  if (step === "list_ads") {
                    setStep("adset");
                    return;
                  }

                  if (step === "confirm") {
                    setStep("list_adsets");
                    return;
                  }

                  // if (step === "create_adset") {
                  //   // setStep("list_adsets");
                  //   return;
                  // }

                  close();
                }}
                next={() => {
                  if (step === "create_campaign") {
                    setStep("create_adset");
                    return;
                  }

                  if (step === "adset") {
                    setStep("confirm");
                    return;
                  }

                  if (step === "create_adset") {
                    setStep("create_creative");
                    return;
                  }

                  if (step === "create_creative") {
                    setStep("list_ads");
                    return;
                  }

                  if (step === "list_adsets" && isCreateCampaign) {
                    setStep("confirm");
                    return;
                  }

                  if (step === "list_ads" && isCreateCampaign) {
                    setStep("confirm");
                    return;
                  }
                }}
                audiences={audiences}
                pixels={pixels}
                search={search}
                campaigns={campaigns}
                adsets={adsets}
                currency={currency}
                createUploadUrl={createUploadUrl}
              />
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          {/* {actions.map((action) => (
            <Button
              key={action.text}
              variant="kydDark"
              isLoading={isLoading}
              w={actions.length === 1 ? "full" : "auto"}
              onClick={() =>
                action.onClick ? onClickAndLoading(action.onClick) : close()
              }
              ml={4}
              {...action.buttonProps}
            >
              {action.text}
            </Button>
          ))} */}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateCampaignWizard;
